import React, { useState, useEffect, useRef } from "react";
import html2canvas from "html2canvas";
import jsPDF from "jspdf";
import { useFinancialIntelligence, useKpis } from "../hooks/useDashboardData";

interface Props {
  filters: any;
}

// ── Inline SVG sparkline ────────────────────────────────────────────────────
const SparkLine: React.FC<{ values: number[]; color: string }> = ({ values, color }) => {
  if (!values || values.length < 2) return null;
  const min = Math.min(...values);
  const max = Math.max(...values);
  const range = max - min || 1;
  const W = 64, H = 28, pad = 3;
  const pts = values.map((v, i) => {
    const x = (i / (values.length - 1)) * W;
    const y = H - pad - ((v - min) / range) * (H - 2 * pad);
    return `${x.toFixed(1)},${y.toFixed(1)}`;
  });
  const lastPt = pts[pts.length - 1].split(",");
  return (
    <svg width={W} height={H} viewBox={`0 0 ${W} ${H}`} style={{ overflow: "visible" }}>
      <polyline
        points={pts.join(" ")}
        fill="none"
        stroke={color}
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <circle cx={lastPt[0]} cy={lastPt[1]} r="2.5" fill={color} />
    </svg>
  );
};

// ── Highlight helper ────────────────────────────────────────────────────────
function renderHighlight(text: string, highlight?: string, type?: string) {
  if (!highlight) return <>{text}</>;
  const idx = text.indexOf(highlight);
  if (idx === -1) return <>{text}</>;
  return (
    <>
      {text.slice(0, idx)}
      <span className={`fi-highlight fi-highlight--${type}`}>{highlight}</span>
      {text.slice(idx + highlight.length)}
    </>
  );
}

// ── Trend arrows ────────────────────────────────────────────────────────────
const TrendUp = () => (
  <svg width="9" height="9" viewBox="0 0 9 9" fill="none">
    <path d="M1.5 6.5L4.5 3L7.5 6.5" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);
const TrendDown = () => (
  <svg width="9" height="9" viewBox="0 0 9 9" fill="none">
    <path d="M1.5 2.5L4.5 6L7.5 2.5" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

// ── Sparkline color per KPI changeType ──────────────────────────────────────
const sparkColor = (changeType: string) =>
  changeType === "positive" ? "#22C358" : "#F87171";

// ── Chip icons ──────────────────────────────────────────────────────────────
const GlobeIcon = () => (
  <svg width="10" height="10" viewBox="0 0 12 12" fill="none" style={{ flexShrink: 0 }}>
    <circle cx="6" cy="6" r="5" stroke="currentColor" strokeWidth="1.2" />
    <ellipse cx="6" cy="6" rx="2.2" ry="5" stroke="currentColor" strokeWidth="1.2" />
    <line x1="1" y1="6" x2="11" y2="6" stroke="currentColor" strokeWidth="1.2" />
  </svg>
);
const CalendarIcon = () => (
  <svg width="10" height="10" viewBox="0 0 12 12" fill="none" style={{ flexShrink: 0 }}>
    <rect x="1" y="2.5" width="10" height="8.5" rx="1.5" stroke="currentColor" strokeWidth="1.2" />
    <line x1="1" y1="5" x2="11" y2="5" stroke="currentColor" strokeWidth="1.2" />
    <line x1="4" y1="1" x2="4" y2="4" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" />
    <line x1="8" y1="1" x2="8" y2="4" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" />
  </svg>
);

// ── Main component ──────────────────────────────────────────────────────────
const FinancialIntelligence: React.FC<Props> = ({ filters }) => {
  const { data, isLoading, error } = useFinancialIntelligence(filters);
  const { data: globalKpis } = useKpis(filters);
  const [summary, setSummary] = useState<string>("");
  const [copied, setCopied] = useState(false);
  const [exporting, setExporting] = useState(false);
  const cardRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (data?.commentary?.executiveSummary) {
      setSummary(data.commentary.executiveSummary);
    }
  }, [data?.commentary?.executiveSummary]);

  const handleExportPdf = async () => {
    const el = cardRef.current;
    if (!el) return;
    setExporting(true);
    try {
      const canvas = await html2canvas(el, { scale: 2, useCORS: true, backgroundColor: "#ffffff" });
      const imgData = canvas.toDataURL("image/png");
      const pdf = new jsPDF({ orientation: "portrait", unit: "px", format: [canvas.width / 2, canvas.height / 2] });
      pdf.addImage(imgData, "PNG", 0, 0, canvas.width / 2, canvas.height / 2);
      pdf.save(`financial-intelligence-commentary.pdf`);
    } finally {
      setExporting(false);
    }
  };

  const handleCopy = () => {
    if (!data) return;
    const text = [
      "Executive Summary",
      summary,
      "",
      "Detailed Analysis",
      ...data.commentary.detailedAnalysis.map((d: any) => `• ${d.label}: ${d.text}`),
    ].join("\n");
    navigator.clipboard.writeText(text).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  if (isLoading || !data) {
    return (
      <div className="fi-page">
        <div className="fi-loading">
          <div className="fi-loading-spinner" />
          <span>Generating AI commentary…</span>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="fi-page">
        <div className="fi-loading" style={{ color: "#DC2626" }}>
          Unable to load Financial Intelligence data.
        </div>
      </div>
    );
  }

  const { commentary, dataIntegrity } = data;
  const kpis = globalKpis ?? [];

  return (
    <div className="fi-page">
      {/* ── Top bar: breadcrumb + title ──────────────────────────────── */}
      <div className="fi-top-bar">
        <div className="fi-breadcrumb-chip">
          <svg width="20" height="20" viewBox="0 0 16 16" fill="none">
            <path d="M8 1.5L9.2 5.8L13.5 7L9.2 8.2L8 12.5L6.8 8.2L2.5 7L6.8 5.8L8 1.5Z" fill="#5663C6" fillOpacity="0.85" />
            <circle cx="12.5" cy="3.5" r="1" fill="#5663C6" fillOpacity="0.5" />
            <circle cx="3.5" cy="11.5" r="0.75" fill="#5663C6" fillOpacity="0.4" />
          </svg>
          <span className="fi-breadcrumb-chip-text">AI Financial Intelligence</span>
        </div>
        <div className="fi-header-row">
          <div>
            <h1 className="fi-page-title">Executive Commentary – AI Assisted</h1>
            <p className="fi-page-subtitle">
              Review and refine AI-generated narrative commentary based on current Q2 performance data across the EMEA region.
            </p>
          </div>
        </div>
      </div>

      {/* ── Body: KPI column + Commentary ───────────────────────────── */}
      <div className="fi-body-grid">

        {/* ── Left KPI column ──────────────────────────────────────── */}
        <div className="fi-kpi-col">
          <div className="fi-kpi-section-label">Key Performance Indicators</div>

          {kpis.map((kpi: any, i: number) => (
            <div key={i} className="fi-kpi-card">
              {/* Row 1: label + sparkline */}
              <div className="fi-kpi-top-row">
                <span className="fi-kpi-label">{kpi.label}</span>
                <SparkLine values={kpi.sparkline} color={sparkColor(kpi.changeType)} />
              </div>
              {/* Row 2: value + badge */}
              <div className="fi-kpi-mid-row">
                <span className="fi-kpi-value">{kpi.value}</span>
                <span className={`fi-kpi-badge fi-kpi-badge--${kpi.changeType}`}>
                  {kpi.trend === "up" ? <TrendUp /> : <TrendDown />}
                  {kpi.change}
                </span>
              </div>
              {/* Row 3: comparison */}
              <div className="fi-kpi-comparison">{kpi.comparison}</div>
            </div>
          ))}

          {/* Data Integrity note */}
          <div className="fi-data-integrity">
            <div className="fi-di-header">
              <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
                <circle cx="6" cy="6" r="5" stroke="#165697" strokeWidth="1.3" />
                <path d="M6 5.5V8.5" stroke="#165697" strokeWidth="1.3" strokeLinecap="round" />
                <circle cx="6" cy="3.8" r="0.65" fill="#165697" />
              </svg>
              <span className="fi-di-label">Data Integrity Note</span>
            </div>
            <p className="fi-di-text">{dataIntegrity}</p>
          </div>
        </div>

        {/* ── Right: AI Commentary card ─────────────────────────────── */}
        <div className="fi-commentary-area">
          <div className="fi-commentary-card" ref={cardRef}>

            {/* ── Card header ──────────────────────────────────────── */}
            <div className="fi-card-header">

              {/* Row 1: title + badge + source link */}
              <div className="fi-card-title-row">
                <div className="fi-card-title-left">
                  <svg width="16" height="16" viewBox="0 0 16 16" fill="none" className="fi-ai-icon">
                    <path d="M8 1.5L9.2 5.8L13.5 7L9.2 8.2L8 12.5L6.8 8.2L2.5 7L6.8 5.8L8 1.5Z" fill="#2590F4" fillOpacity="0.85" />
                    <circle cx="12.5" cy="3.5" r="1" fill="#2590F4" fillOpacity="0.5" />
                    <circle cx="3.5" cy="11.5" r="0.75" fill="#2590F4" fillOpacity="0.4" />
                  </svg>
                  <span className="fi-card-title">AI-Assisted Commentary</span>
                  <span className="fi-review-badge">Review Required</span>
                </div>
                <a href="#!" className="fi-source-link-small">View Source Data →</a>
              </div>

              {/* Row 2: generated date + model */}
              <div className="fi-card-meta-row">
                <span className="fi-card-meta-item">
                  <svg width="11" height="11" viewBox="0 0 12 12" fill="none">
                    <circle cx="6" cy="6" r="5" stroke="#94a3b8" strokeWidth="1.2" />
                    <path d="M6 3.5V6l2 1.5" stroke="#94a3b8" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                  Generated: {commentary.generatedAt}
                </span>
                <span className="fi-card-meta-item">
                  <svg width="11" height="11" viewBox="0 0 12 12" fill="none">
                    <path d="M10 2a5 5 0 1 0 .5 6.5" stroke="#94a3b8" strokeWidth="1.2" strokeLinecap="round" />
                    <path d="M10 2v3h-3" stroke="#94a3b8" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                  Model: Finance-GPT 4.5
                </span>
              </div>

              {/* Row 3: filter chips */}
              <div className="fi-card-chips-row">
                <span className="fi-filters-label">
                  <svg width="11" height="11" viewBox="0 0 12 12" fill="none">
                    <path d="M1 2.5h10M3 6h6M5 9.5h2" stroke="#165697" strokeWidth="1.3" strokeLinecap="round" />
                  </svg>
                  FILTERS APPLIED:
                </span>
                {commentary.filters.map((f: string, i: number) => (
                  <span key={i} className="fi-chip">
                    {i === 0 && <GlobeIcon />}
                    {i === 1 && <CalendarIcon />}
                    {f}
                  </span>
                ))}
              </div>
            </div>

            {/* ── Card body ────────────────────────────────────────── */}
            <div className="fi-card-body">

              {/* Executive Summary */}
              <div className="fi-section">
                <div className="fi-section-title-row">
                  <span className="fi-section-title">Executive Summary</span>
                </div>
                <textarea
                  className="fi-summary-textarea"
                  value={summary}
                  onChange={(e) => setSummary(e.target.value)}
                  rows={4}
                />
              </div>

              {/* Detailed Analysis */}
              <div className="fi-section">
                <div className="fi-section-title-row">
                  <span className="fi-section-title">Detailed Analysis</span>
                </div>
                <ul className="fi-analysis-list">
                  {commentary.detailedAnalysis.map((item: any, i: number) => (
                    <li key={i} className="fi-analysis-item">
                      <span className="fi-analysis-dot" />
                      <span>
                        <strong className={`fi-analysis-label fi-analysis-label--${item.highlightType ?? "neutral"}`}>{item.label}: </strong>
                        {renderHighlight(item.text, item.highlight, item.highlightType)}
                      </span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>

            {/* ── Card footer ──────────────────────────────────────── */}
            <div className="fi-card-footer">
              <div className="fi-footer-left">
                <button className="fi-action-btn" onClick={handleCopy}>
                  <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
                    <rect x="4.5" y="1" width="8" height="10" rx="1.5" stroke="currentColor" strokeWidth="1.3" />
                    <rect x="1.5" y="3" width="8" height="10" rx="1.5" fill="white" stroke="currentColor" strokeWidth="1.3" />
                  </svg>
                  {copied ? "Copied!" : "Copy to Clipboard"}
                </button>
                <button className="fi-action-btn fi-action-btn--pdf" onClick={handleExportPdf} disabled={exporting}>
                  <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
                    <rect x="2" y="1" width="10" height="12" rx="1.5" stroke="currentColor" strokeWidth="1.3" />
                    <path d="M5 4.5h4M5 7h4M5 9.5h2.5" stroke="currentColor" strokeWidth="1.1" strokeLinecap="round" />
                  </svg>
                  {exporting ? "Exporting…" : "Export to PDF"}
                </button>
              </div>
              <button className="fi-approve-btn">
                Approve &amp; Distribute
                <svg width="12" height="12" viewBox="0 0 12 12" fill="none" style={{ marginLeft: 6 }}>
                  <path d="M4.5 2.5L8.5 6L4.5 9.5" stroke="white" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              </button>
            </div>

            {/* ── Disclaimer ───────────────────────────────────────── */}
            <div className="fi-disclaimer">
              <svg width="13" height="13" viewBox="0 0 13 13" fill="none" style={{ flexShrink: 0, marginTop: 1 }}>
                <circle cx="6.5" cy="6.5" r="5.5" stroke="#94a3b8" strokeWidth="1.2" />
                <path d="M6.5 5.8V9" stroke="#94a3b8" strokeWidth="1.2" strokeLinecap="round" />
                <circle cx="6.5" cy="4.2" r="0.65" fill="#94a3b8" />
              </svg>
              <p className="fi-disclaimer-text">
                Disclaimer: AI-assisted draft for decision support only. This narrative is generated based on raw financial data and should be validated by the respective Regional Finance Controllers before formal distribution to the Board of Directors. Human validation ensures contextual accuracy that automated systems may omit.
              </p>
            </div>
          </div>

          {/* ── Page footer ──────────────────────────────────────────── */}
          <div className="fi-page-footer">
            <span className="fi-footer-copy">© {new Date().getFullYear()} Finance Operating System</span>
            <div className="fi-footer-links">
              <a href="#!" className="fi-footer-link">Privacy Policy</a>
              <span className="fi-footer-sep">|</span>
              <a href="#!" className="fi-footer-link">System Guides</a>
              <span className="fi-footer-sep">|</span>
              <a href="#!" className="fi-footer-link">Author</a>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default FinancialIntelligence;
